/**
 * 出生时间工具：把「未知」出生时间替换为可计算的默认值。
 *
 * 背景：出生时间选择器允许用户选「未知」时辰/分钟，表单展示保留「未知」文本
 * （如 "1990-01-01 未知:00"）。但排盘/保存档案时服务端与本地引擎都无法解析
 * 「未知」字符串，需在计算前替换为默认值——未知时辰 → 12:00，未知分钟 → 00
 * （与出生时间选择器提示语「未知出生時辰，可默認12:00」一致；地支午时辰起算
 * 小时也是 12:00）。
 */

/**
 * 解析出生时间中的「未知」标记并替换为默认值。
 *
 * @param birthTime 出生时间，如 "1990-01-01 未知:00" / "1990-01-01 08:未知"
 * @returns 替换后的可计算时间，如 "1990-01-01 12:00" / "1990-01-01 08:00"；
 *          不含「未知」则原样返回
 */
export function resolveUnknownBirthTime(birthTime: string): string {
  if (!birthTime || !birthTime.includes('未知')) return birthTime;
  const [datePart, timePart = '12:00'] = birthTime.split(/[ T]/);
  const [hRaw = '12', mRaw = '00'] = timePart.split(':');
  const h = hRaw.includes('未知') ? '12' : hRaw;
  const m = mRaw.includes('未知') ? '00' : mRaw;
  const pad = (n: string) => n.padStart(2, '0');
  return `${datePart} ${pad(h)}:${pad(m)}`;
}

// 档案表单新增命主时的占位默认值（日期本身无意义，仅作初始展示）
const PLACEHOLDER_BIRTH_TIMES = new Set(['1993-01-01 00:00', '1993-01-01T00:00', '1993-01-01']);

/**
 * 判断出生时间是否携带真实选定的小时。
 *
 * 空值、仅日期、或档案表单占位默认（1993-01-01 00:00）均视为尚未选择真实小时，
 * 供出生时间选择器在这些情况下把小时默认到 12:00（公历）/ 午时（农历）。
 * 真实出生时间（含 00:00）一律视为有真实小时，保持原值。
 */
export function hasRealBirthHour(birthTime: string): boolean {
  if (!birthTime) return false;
  const normalized = birthTime.trim().replace('T', ' ');
  if (PLACEHOLDER_BIRTH_TIMES.has(normalized)) return false;
  const parts = normalized.split(' ');
  if (parts.length < 2 || !parts[1]) return false;
  const h = parseInt(parts[1].split(':')[0], 10);
  return !Number.isNaN(h) && h >= 0 && h <= 23;
}
